import { Button } from "@/components/atoms/Button";
import { Image } from "@/components/atoms/Image";
import { Text } from "@/components/atoms/Text";
import { CounterInputField } from "@/components/molecules/CounterInput";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/molecules/ExpandableContent";
import CheckCircleFilledIcon from "@/icons/__generated__/CheckCircleFilled/CheckCircleFilled24";
import { useWindowSize } from "@/lib/hooks/use-window-size";
import { cn } from "@/lib/utils";
import * as React from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, IntlShape } from "react-intl";
import { AddOnItem } from "./add-on-counter-types";
import {
  getCategoryIcon,
  getCategoryString,
  getCostString,
} from "./add-on-utils";
import { AddOnFeesPopover } from "./AddOnFeesPopover";

export type AddOnCounterProps = {
  name: string;
  className?: string;
  testId?: string;
  addOnItem: AddOnItem;
  intl: IntlShape;
  disabled?: boolean;
};

export const AddOnCounter = ({
  name,
  className,
  addOnItem,
  testId = "add-on-counter",
  disabled,
  intl,
}: AddOnCounterProps) => {
  const { watch } = useFormContext();
  const value = watch(name);

  const [isExpanded, setIsExpanded] = React.useState(false);

  const descriptionRef = React.useRef<HTMLDivElement>(null);
  const [descriptionIsTruncated, setDescriptionIsTruncated] =
    React.useState(false);

  const windowSize = useWindowSize();

  React.useEffect(() => {
    if (!descriptionRef.current) {
      return;
    }

    setDescriptionIsTruncated(descriptionRef.current.scrollHeight > 24);
  }, [addOnItem.description, windowSize.width]);

  return (
    <div
      className={cn(
        "tw-p-16 tw-border tw-border-gray-300 tw-rounded-6 tw-bg-base-white",
        {
          "tw-border-gray-900": value > 0,
        },
        className,
      )}
    >
      <div className="tw-flex tw-gap-16">
        <div className="tw-max-w-80 tw-min-w-80 tw-h-80 tw-rounded-4 tw-relative">
          {value > 0 && (
            <span className="tw-absolute tw-left-0 tw-top-0 -tw-translate-x-1/3 -tw-translate-y-1/3 tw-h-[24px] tw-w-[24px] tw-z-20 tw-rounded-circular tw-bg-base-white tw-inline-flex tw-items-center tw-justify-center">
              <CheckCircleFilledIcon />
            </span>
          )}
          {!!addOnItem.image ? (
            <Image
              loading="lazy"
              testId={testId ? `${testId}-image` : undefined}
              src={addOnItem.image}
              alt={addOnItem.label}
              className="tw-rounded-4 tw-relative tw-z-10"
            />
          ) : (
            <div className="tw-size-full tw-flex tw-items-center tw-justify-center tw-bg-gray-200 tw-text-gray-600 tw-rounded-4">
              {getCategoryIcon(addOnItem.categoryId)}
            </div>
          )}
        </div>

        <div className="tw-min-w-0 tw-flex-1">
          <Text
            typeface="body-2-m"
            className="tw-mb-4"
            testId={testId ? `${testId}-label` : undefined}
          >
            {addOnItem.label}
          </Text>

          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            {!isExpanded && descriptionIsTruncated && (
              <div className="tw-flex tw-items-center">
                <Text
                  typeface="body-1-long"
                  className="tw-text-gray-600 tw-w-full tw-truncate"
                  testId={testId ? `${testId}-description` : undefined}
                >
                  {addOnItem.description}
                </Text>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="text-only"
                    className="tw-body-1-long tw-text-gray-600 tw-underline"
                    testId={testId ? `${testId}-trigger` : undefined}
                  >
                    <FormattedMessage defaultMessage="See more" />
                  </Button>
                </CollapsibleTrigger>
              </div>
            )}

            <CollapsibleContent
              ref={descriptionRef}
              forceMount={true}
              className={cn({
                "tw-h-0 tw-overflow-hidden":
                  !isExpanded && descriptionIsTruncated,
              })}
            >
              <Text typeface="body-1-long" className="tw-text-gray-600">
                {addOnItem.description}
              </Text>
            </CollapsibleContent>

            {isExpanded && (
              <CollapsibleTrigger asChild>
                <Button
                  variant="text-only"
                  className="tw-body-1-long tw-text-gray-600 tw-underline"
                >
                  <FormattedMessage defaultMessage="See less" />
                </Button>
              </CollapsibleTrigger>
            )}
          </Collapsible>

          <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-6 tw-mt-8">
            <div className="tw-flex tw-items-center ">
              <Text
                typeface="body-2-m"
                testId={testId ? `${testId}-cost` : undefined}
              >
                {getCostString(intl, addOnItem)}
              </Text>

              {!!addOnItem.fees?.length && (
                <div className="tw-ml-4 tw-flex tw-items-center">
                  <span className="tw-text-gray-600 tw-mr-4 tw-body-1">+</span>
                  <AddOnFeesPopover
                    fees={addOnItem.fees}
                    addOnName={addOnItem.label}
                    currency={addOnItem.currency}
                  />
                </div>
              )}

              {!!addOnItem.max && (
                <>
                  <Text
                    prefix={
                      <span className="tw-size-2 tw-bg-gray-600 tw-inline-block tw-rounded-circular" />
                    }
                    spacing={6}
                    typeface="body-1"
                    className="tw-text-gray-600 tw-ml-6"
                    testId={testId ? `${testId}-max` : undefined}
                  >
                    <FormattedMessage
                      defaultMessage="Max {max}"
                      values={{ max: addOnItem.max }}
                    />
                  </Text>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="tw-flex-col tw-justify-between tw-items-end tw-hidden md:tw-flex">
          <Text
            typeface="body-1"
            className="tw-bg-gray-200 tw-py-6 tw-px-8 tw-rounded-4 tw-whitespace-nowrap"
          >
            {getCategoryString(intl, addOnItem.categoryId)}
          </Text>

          <CounterInputField
            disabled={disabled}
            testId={testId}
            name={name}
            max={addOnItem.max}
          />
        </div>
      </div>

      <div className="tw-mt-16 md:tw-hidden">
        <CounterInputField
          disabled={disabled}
          name={name}
          max={addOnItem.max}
          fullWidth={true}
        />
      </div>
    </div>
  );
};
